import React, { useEffect, useMemo, useRef } from 'react';
import { Button, Empty, Image, Tooltip } from 'antd';
import { CheckOutlined, ExclamationCircleOutlined, ClockCircleOutlined, FileOutlined, DownloadOutlined, SwapOutlined, MobileOutlined, RobotOutlined } from '@ant-design/icons';
import { formatDayLabel, formatFullTime, formatMessageTime, sortMessages } from './liveChatMeta';
import VoicePlayer from './VoicePlayer';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('fa-IR')} کیلوبایت`;
  return `${Number((bytes / 1024 / 1024).toFixed(1)).toLocaleString('fa-IR')} مگابایت`;
};

/** پیوست‌های پیام: تصاویر با پیش‌نمایش، فایل‌ها به‌صورت لینک دانلود */
const Attachments = ({ items, own }) => {
  const images = items.filter((a) => a.kind === 'image' && a.url);
  const voices = items.filter((a) => a.kind === 'audio' && a.url);
  const files = items.filter((a) => a.kind !== 'image' && a.kind !== 'audio' && a.url);
  return (
    <div className="flex flex-col gap-1.5 mb-1">
      {images.length > 0 && (
        <Image.PreviewGroup>
          <div className={`grid gap-1 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {images.map((img) => (
              <Image
                key={img.url}
                src={img.url}
                alt={img.name}
                width={images.length > 1 ? 120 : 220}
                height={images.length > 1 ? 120 : undefined}
                style={{ objectFit: 'cover', borderRadius: 10, maxHeight: 240, maxWidth: '100%' }}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      )}
      {voices.map((voice) => (
        <VoicePlayer key={voice.url} src={voice.url} duration={voice.duration} own={own} />
      ))}
      {files.map((file) => (
        <a
          key={file.url}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-2 rounded-lg px-2 py-1.5 no-underline ${
            own ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-primary-50 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-primary-100'
          }`}
        >
          <FileOutlined className="text-lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium" dir="ltr">{file.name}</span>
            {file.size ? <span className="block text-[10px] opacity-70">{formatSize(file.size)}</span> : null}
          </span>
          <DownloadOutlined />
        </a>
      ))}
    </div>
  );
};

const StatusIcon = ({ status }) => {
  if (status === 'failed') return <ExclamationCircleOutlined className="text-red-400" />;
  if (status === 'pending') return <ClockCircleOutlined />;
  return <CheckOutlined />;
};

/** جداکننده روز بین پیام‌ها */
const DaySeparator = ({ value }) => (
  <div className="flex items-center gap-2 my-1 custom-font" role="separator">
    <div className="flex-1 h-px bg-primary-100 dark:bg-white/10" />
    <span className="text-[11px] text-gray-500 dark:text-gray-400 rounded-full bg-white/80 dark:bg-white/5 border border-primary-100 dark:border-white/10 px-3 py-0.5">
      {formatDayLabel(value)}
    </span>
    <div className="flex-1 h-px bg-primary-100 dark:bg-white/10" />
  </div>
);

/**
 * لیست حباب‌های پیام (به ترتیب زمان ارسال، با جداکننده روز).
 * perspective="operator": پیام‌های خروجی (اپراتور) سمت شروع؛ perspective="customer": پیام‌های ورودی (مشتری) سمت شروع.
 * externalLabel: برچسب پیام‌های خروجی که بیرون از CRM فرستاده شده‌اند (مثلاً «اپ اینستاگرام»).
 */
const ChatMessageList = ({ messages: rawMessages, perspective = 'operator', hasMore = false, onLoadMore, loadingMore = false, externalLabel = 'خارج از CRM' }) => {
  const messages = useMemo(() => sortMessages(rawMessages), [rawMessages]);
  const bottomRef = useRef(null);
  const prevLastId = useRef(null);
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const lastId = lastMessage?.id ?? null;
  const ownDirection = perspective === 'operator' ? 'out' : 'in';

  // فقط همان ظرف اسکرول پیام‌ها جابه‌جا می‌شود؛ scrollIntoView کل صفحه و لایه‌های والد را هم اسکرول می‌کرد
  useEffect(() => {
    const anchor = bottomRef.current;
    if (!anchor || lastId == null) return;
    let scroller = anchor.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const firstLoad = prevLastId.current == null;
    const nearBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 160;
    const isOwn = lastMessage?.direction === ownDirection;
    if (firstLoad || nearBottom || isOwn) {
      scroller.scrollTop = scroller.scrollHeight;
    }
    prevLastId.current = lastId;
  }, [lastId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** یک حباب پیام (یا رویداد ارجاع / پیام سیستمی) */
  const renderMessage = (m, own) => {
    if (m.type === 'transfer') {
      return (
        <div key={m.id} className="flex justify-center">
          <div className="max-w-[85%] rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 custom-font">
            <div className="flex items-center gap-1.5 font-medium">
              <SwapOutlined />
              <span>
                ارجاع از {m.meta?.from_name || m.sender?.name || '—'} به {m.meta?.to_name || '—'}
              </span>
              <span className="text-[10px] opacity-70 ms-2">{formatMessageTime(m.created_at)}</span>
            </div>
            {m.meta?.note && <div className="mt-1 whitespace-pre-wrap break-words leading-5">{m.meta.note}</div>}
            <div className="mt-1 text-[10px] opacity-70">یادداشت داخلی — برای مشتری نمایش داده نمی‌شود</div>
          </div>
        </div>
      );
    }
    if (m.sender_type === 'system') {
      return (
        <div key={m.id} className="text-center text-xs text-gray-400 custom-font">
          {m.body}
        </div>
      );
    }
    return (
      <div key={m.id} className={`flex ${own ? 'justify-start' : 'justify-end'}`}>
        <div
          className={`max-w-[85%] sm:max-w-[75%] min-w-0 rounded-2xl px-3 sm:px-4 py-2 shadow-sm custom-font [overflow-wrap:anywhere] ${
            own
              ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-ee-sm'
              : 'bg-white dark:bg-[#0f1b22] border border-primary-100 dark:border-[rgba(13,136,126,0.25)] text-gray-800 dark:text-gray-100 rounded-es-sm'
          }`}
        >
          {m.direction === 'out' && m.sender?.name && (
            <div className={`text-[11px] mb-1 ${own ? 'text-white/70' : 'text-gray-400'}`}>{m.sender.name}</div>
          )}
          {m.direction === 'out' && !m.sender && m.meta?.origin === 'external' && (
            <div className={`text-[11px] mb-1 flex items-center gap-1 ${own ? 'text-white/70' : 'text-gray-400'}`}>
              <MobileOutlined /> {externalLabel}
            </div>
          )}
          {m.direction === 'out' && !m.sender && m.meta?.origin === 'auto_reply' && (
            <div className={`text-[11px] mb-1 flex items-center gap-1 ${own ? 'text-white/70' : 'text-gray-400'}`}>
              <RobotOutlined /> پاسخ خودکار به کامنت
            </div>
          )}
          {m.attachments?.length > 0 && <Attachments items={m.attachments} own={own} />}
          {m.body && <div className="whitespace-pre-wrap break-words text-sm leading-6">{m.body}</div>}
          <div className={`flex items-center gap-1 text-[10px] mt-1 ${own ? 'text-white/70' : 'text-gray-400'}`}>
            <Tooltip title={formatFullTime(m.created_at)}>
              <time dateTime={m.created_at}>{formatMessageTime(m.created_at)}</time>
            </Tooltip>
            {m.direction === 'out' && <StatusIcon status={m.status} />}
          </div>
        </div>
      </div>
    );
  };

  if (!messages.length) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Empty description={<span className="custom-font">هنوز پیامی نیست</span>} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-4">
      {hasMore && (
        <div className="text-center">
          <Button size="small" onClick={onLoadMore} loading={loadingMore} className="custom-font">
            پیام‌های قبلی
          </Button>
        </div>
      )}
      {messages.map((m, index) => {
        const own = m.direction === ownDirection;
        const prev = messages[index - 1];
        const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
        return (
          <React.Fragment key={m.id}>
            {newDay && <DaySeparator value={m.created_at} />}
            {renderMessage(m, own)}
          </React.Fragment>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessageList;
